import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router';
import { Box, Typography, Skeleton } from '@mui/material';
import FundraisingTableCard from 'features/IRWorkstation/components/Fundraising/FundraisingTableCard';
import FundraisingFilters from 'features/IRWorkstation/components/Fundraising/FundraisingFilters';
import IRDataGrid from 'features/IRWorkstation/components/Common/IRDataGrid';
import buildFundraisingGridColumns from 'features/IRWorkstation/components/Fundraising/fundraisingGridColumns';
import useEntity from 'features/IRWorkstation/hooks/useEntity';
import useFundraisings from 'features/IRWorkstation/hooks/useFundraisings';
import useFundraisingFilters from 'features/IRWorkstation/hooks/useFundraisingFilters';
import useFundraisingSort from 'features/IRWorkstation/hooks/useFundraisingSort';
import useFundraisingStageSummary from 'features/IRWorkstation/hooks/useFundraisingStageSummary';
import ActiveRaiseContent from 'features/IRWorkstation/components/Entity/ActiveRaiseContent';
import useSalesforceRaiseMetrics from 'hooks/useSalesforceRaiseMetrics';
import { useIRWorkstation } from '../contexts/IRWorkstationContext';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';

const EntityDetail = ({ onOpenDrawer: _onOpenDrawer }) => {
	const { tenantId, entityId } = useParams();
	const { pathname } = useLocation();
	const { tenant } = useIRWorkstation();
	const { trackButtonClick, trackPageLoad } = useTrackEvent({ tenant, product: 'IEM' });
	const { filters, setFilters } = useFundraisingFilters();
	const { sort, applySort } = useFundraisingSort();
	const {
		entity,
		loading: entityLoading,
		error: entityError,
	} = useEntity(entityId);
	const {
		fundraisings,
		totalCount,
		loading: fundraisingsLoading,
		loadingMore,
		hasMore,
		error: fundraisingsError,
		loadMore,
		refresh,
	} = useFundraisings(entityId, filters, sort);
	const {
		metrics: raiseMetrics,
		loading: raiseMetricsLoading,
		error: raiseMetricsError,
		refetch: refetchRaiseMetrics,
	} = useSalesforceRaiseMetrics(entityId);
	const {
		stageSummary,
		loading: stageSummaryLoading,
		error: stageSummaryError,
		refetch: refetchStageSummary,
	} = useFundraisingStageSummary(entityId, filters);

	// The KPIs and the stage chart live in the same panel, so it loads, errors and
	// retries as one unit rather than showing half a panel
	const raisePanelLoading = raiseMetricsLoading || stageSummaryLoading;
	const raisePanelError = raiseMetricsError || stageSummaryError;
	const refetchRaisePanel = useCallback(() => {
		refetchRaiseMetrics();
		refetchStageSummary();
	}, [refetchRaiseMetrics, refetchStageSummary]);

	const error = entityError || fundraisingsError;

	const handleButtonClickTrackEvent = useCallback(
		(targetKey) => {
			const targetMap = {
				typeFilter: ButtonClickTargets.RaiseDashboardTypeFilter,
				activeFilter: ButtonClickTargets.RaiseDashboardActiveFilter,
				existingInvestorFilter:
					ButtonClickTargets.RaiseDashboardExistingInvestorFilter,
				stageFilter: ButtonClickTargets.RaiseDashboardStageFilter,
				closingRoundsFilter:
					ButtonClickTargets.RaiseDashboardClosingRoundsFilter,
				fromDateFilter: ButtonClickTargets.RaiseDashboardFromDateFilter,
				toDateFilter: ButtonClickTargets.RaiseDashboardToDateFilter,
				edit: ButtonClickTargets.RaiseDashboardEdit,
				convertToInvestor: ButtonClickTargets.RaiseDashboardConvertToInvestor,
				viewInCrm: ButtonClickTargets.RaiseDashboardViewRecordInCrm,
			};

			const target = targetMap[targetKey];
			if (target) {
				trackButtonClick(target);
			}
		},
		[trackButtonClick]
	);

	const handleFiltersChange = useCallback(
		(newFilters) => {
			// Track which filter changed
			if (newFilters.investor_type !== filters.investor_type) {
				handleButtonClickTrackEvent('typeFilter');
			}
			if (newFilters.active !== filters.active) {
				handleButtonClickTrackEvent('activeFilter');
			}
			if (newFilters.existing_investor !== filters.existing_investor) {
				handleButtonClickTrackEvent('existingInvestorFilter');
			}
			if (newFilters.stage !== filters.stage) {
				handleButtonClickTrackEvent('stageFilter');
			}
			if (newFilters.closing_round !== filters.closing_round) {
				handleButtonClickTrackEvent('closingRoundsFilter');
			}
			if (newFilters.commitment_date_from !== filters.commitment_date_from) {
				handleButtonClickTrackEvent('fromDateFilter');
			}
			if (newFilters.commitment_date_to !== filters.commitment_date_to) {
				handleButtonClickTrackEvent('toDateFilter');
			}

			setFilters(newFilters);
		},
		[filters, handleButtonClickTrackEvent, setFilters]
	);

	// The LP360 page reads the entity name and this path from router state to
	// render its "back to fundraise dashboard" breadcrumb.
	const entityName = entity?.name;
	const getAccountLink = useCallback(
		(row) => ({
			to: `/tenants/${tenantId}/workstation/lps/${row.account_id}`,
			state: { from: { label: entityName, pathname } },
		}),
		[tenantId, entityName, pathname]
	);

	// The skeleton grid renders inert columns — the real table's columns are
	// built inside FundraisingTableCard with its menu handler.
	const columns = useMemo(
		() => buildFundraisingGridColumns(() => {}, getAccountLink),
		[getAccountLink]
	);

	const sortModel = useMemo(
		() => (sort.sortBy ? [{ field: sort.sortBy, sort: sort.sortDir }] : []),
		[sort.sortBy, sort.sortDir]
	);

	const refreshData = () => {
		refetchRaiseMetrics({ showLoading: false });
		refetchStageSummary();
		return refresh();
	};

	const filterByStage = (stage) => {
		// Clear filter if clicking the same stage
		if (filters.stage === stage) {
			handleFiltersChange({ ...filters, stage: '' });
			return;
		}
		handleFiltersChange({ ...filters, stage });
	};

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.RaiseDashboard);
	}, []);

	if (error) {
		return (
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: '20px',
				}}
			>
				<Typography color='error'>Failed to load entity details</Typography>
			</Box>
		);
	}

	// Full-page skeleton only while the entity itself is unknown. Reloading the table
	// (a filter or sort change) keeps the header and the raise panel on screen and
	// swaps just the table rows for skeletons.
	if (entityLoading) {
		return (
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					minHeight: 0,
					overflowY: 'auto',
				}}
			>
				{/* Header Skeleton */}
				<Box
					sx={{
						padding: '16px 15px',
						backgroundColor: 'background.default',
						flexShrink: 0,
					}}
					data-testid='skeleton-header'
				>
					<Skeleton variant='text' width={200} height={24} />
				</Box>

				<Box sx={{ flexShrink: 0 }}>
					<ActiveRaiseContent
						metrics={raiseMetrics}
						stageSummary={stageSummary}
						loading={raisePanelLoading}
						error={raisePanelError}
						refetch={refetchRaisePanel}
						kpiVariant='default'
						showReupConversion
						onStageClick={filterByStage}
					/>
				</Box>

				{/* Table Container Skeleton */}
				<Box
					sx={{
						flexShrink: 0,
						padding: '0 15px 16px 15px',
						backgroundColor: 'background.default',
						display: 'flex',
						flexDirection: 'column',
						gap: '14px',
					}}
				>
					<Box
						sx={{
							backgroundColor: 'background.paper',
							border: '0.5px solid',
							borderColor: 'border.light',
							borderRadius: '12px',
							overflow: 'hidden',
						}}
					>
						{/* Table Header Skeleton */}
						<Box
							sx={{
								padding: '10px 14px',
								borderBottom: '0.5px solid',
								borderColor: 'border.light',
							}}
						>
							<Skeleton variant='text' width={100} height={16} />
						</Box>
						<IRDataGrid
							rows={[]}
							columns={columns}
							loading
							dataTestId='fundraisings-grid-skeleton'
						/>
					</Box>
				</Box>
			</Box>
		);
	}

	return (
		// The page scrolls so the KPIs and the stage chart can be moved out of the
		// way to reach the pipeline grid on shorter screens. The grid itself keeps a
		// minimum height, which is what forces that page scroll rather than letting
		// the grid collapse to nothing.
		<Box
			data-testid='fundraisings-scroll-container'
			sx={{
				flex: 1,
				display: 'flex',
				flexDirection: 'column',
				minHeight: 0,
				overflowY: 'auto',
				overflowX: 'auto',
			}}
		>
			{/* Header */}
			<Box
				sx={{
					padding: { xs: '12px', sm: '16px 15px' },
					backgroundColor: 'background.default',
					display: 'flex',
					alignItems: 'baseline',
					flexShrink: 0,
				}}
			>
				<Typography sx={{ fontSize: '17px', fontWeight: 700 }}>
					{entity?.name || 'Entity Name'} Fundraise Dashboard
				</Typography>
			</Box>

			<Box sx={{ flexShrink: 0 }}>
				<ActiveRaiseContent
					metrics={raiseMetrics}
					stageSummary={stageSummary}
					loading={raisePanelLoading}
					error={raisePanelError}
					refetch={refetchRaisePanel}
					kpiVariant='default'
					showReupConversion
					onStageClick={filterByStage}
				/>
			</Box>

			{/* Grid Container */}
			<Box
				sx={{
					padding: { xs: '0 12px 12px 12px', sm: '0 15px 16px 15px' },
					backgroundColor: 'background.default',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<FundraisingTableCard
					title='LP Pipeline'
					totalCount={totalCount}
					filterBar={
						<FundraisingFilters
							filters={filters}
							onChange={handleFiltersChange}
							entityId={entityId}
						/>
					}
					getAccountLink={getAccountLink}
					rows={fundraisings}
					loading={fundraisingsLoading}
					loadingMore={loadingMore}
					hasMore={hasMore}
					sortModel={sortModel}
					onSortModelChange={applySort}
					onRowsScrollEnd={loadMore}
					noRowsMessage='No fundraising records found'
					dataTestId='fundraisings-grid'
					onRefresh={refreshData}
					onMenuAction={handleButtonClickTrackEvent}
				/>
			</Box>
		</Box>
	);
};

EntityDetail.propTypes = {
	onOpenDrawer: PropTypes.func,
};

export default EntityDetail;
